/**
 * Attribution Tracker
 *
 * Captures and persists marketing attribution data from URL parameters:
 * - UTM parameters (utm_source, utm_campaign, etc.)
 * - Ad network click IDs (fbclid, ttclid, rdt_cid)
 * - Landing page information
 *
 * This works alongside AppsFlyer Smart Script to maintain attribution
 * context across the user journey.
 */

const STORAGE_KEY = "chatbcc_attribution"
const FIRST_TOUCH_KEY = "chatbcc_attribution_first"

export interface AttributionData {
  clickId?: string
  clickIdSource?: "meta" | "tiktok" | "reddit"
  utmSource?: string
  utmCampaign?: string
  utmMedium?: string
  utmContent?: string
  utmAdset?: string
  landingPageUrl?: string
  timestamp: string
}

/**
 * Extract attribution data from URL parameters
 */
const extractAttributionFromUrl = (): Partial<AttributionData> | null => {
  const params = new URLSearchParams(window.location.search)

  // Extract click IDs - prioritize in order: Meta, TikTok, Reddit
  const clickIdMap = {
    fbclid: "meta" as const,
    ttclid: "tiktok" as const,
    rdt_cid: "reddit" as const,
  }

  let clickId: string | undefined
  let clickIdSource: "meta" | "tiktok" | "reddit" | undefined

  for (const [param, source] of Object.entries(clickIdMap)) {
    const value = params.get(param)
    if (value) {
      clickId = value
      clickIdSource = source
      break
    }
  }

  // Extract UTM parameters
  const utmSource = params.get("utm_source") || undefined
  const utmCampaign = params.get("utm_campaign") || undefined
  const utmMedium = params.get("utm_medium") || undefined
  const utmContent = params.get("utm_content") || undefined
  const utmAdset = params.get("utm_adset") || undefined

  // Only return if we have at least one attribution parameter
  if (!clickId && !utmSource && !utmCampaign) {
    return null
  }

  return {
    clickId,
    clickIdSource,
    utmSource,
    utmCampaign,
    utmMedium,
    utmContent,
    utmAdset,
    landingPageUrl: window.location.href,
    timestamp: `${Date.now()}`,
  }
}

/**
 * Get attribution data from localStorage
 */
const getStoredAttribution = (key: string): AttributionData | null => {
  try {
    const stored = localStorage.getItem(key)
    return stored ? JSON.parse(stored) : null
  } catch {
    return null
  }
}

/**
 * Store attribution data in localStorage
 */
const storeAttribution = (key: string, data: AttributionData): void => {
  try {
    localStorage.setItem(key, JSON.stringify(data))
  } catch (e) {
    console.error("Failed to store attribution data:", e)
  }
}

/**
 * Initialize attribution tracking on page load
 * Captures first-touch and last-touch attribution
 */
export const initAttributionTracking = (): void => {
  const currentAttribution = extractAttributionFromUrl()

  if (currentAttribution) {
    const fullData: AttributionData = {
      ...currentAttribution,
      timestamp: `${Date.now()}`,
    }

    // Always update last-touch
    storeAttribution(STORAGE_KEY, fullData)

    // Only set first-touch if not already set
    const firstTouch = getStoredAttribution(FIRST_TOUCH_KEY)
    if (!firstTouch) {
      storeAttribution(FIRST_TOUCH_KEY, fullData)
    }
  }
}

/**
 * Get the last-touch attribution data
 */
export const getLastTouchAttribution = (): AttributionData | null => {
  return getStoredAttribution(STORAGE_KEY)
}

/**
 * Get the first-touch attribution data
 */
export const getFirstTouchAttribution = (): AttributionData | null => {
  return getStoredAttribution(FIRST_TOUCH_KEY)
}

/**
 * Clear all attribution data (useful for testing)
 */
export const clearAttribution = (): void => {
  try {
    localStorage.removeItem(STORAGE_KEY)
    localStorage.removeItem(FIRST_TOUCH_KEY)
  } catch (e) {
    console.error("Failed to clear attribution data:", e)
  }
}
